import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import PageHeader from '../components/PageHeader';
import { DAMAGE_TYPES, DAMAGE_SEVERITIES, DAMAGE_STATUSES } from '../data/damages';

const TYPE_BY_ID     = Object.fromEntries(DAMAGE_TYPES.map(t => [t.id, t]));
const SEVERITY_BY_ID = Object.fromEntries(DAMAGE_SEVERITIES.map(s => [s.id, s]));
const STATUS_BY_ID   = Object.fromEntries(DAMAGE_STATUSES.map(s => [s.id, s]));

function StatCard({ label, value, color, sub, icon, tone }) {
  // tone: 'ac' | 'gr' | 'am' | 'bl' | 'pu' (matches CSS classes)
  return (
    <div className="sc-2" style={{ flex: 1, minWidth: 0 }}>
      {icon && (
        <div className={`sc-2-ico ${tone || ''}`}>{icon}</div>
      )}
      <div className="sc-2-body">
        <div className="sc-2-lb">{label}</div>
        <div className={`sc-2-vl ${tone || ''}`}>{value}</div>
        {sub && <div className="sc-2-sub">{sub}</div>}
      </div>
    </div>
  );
}

function StatusPill({ statusId }) {
  const s = STATUS_BY_ID[statusId] || { label: statusId, color: 'var(--tx3)', bg: 'var(--sur2)' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 10,
      background: s.bg, color: s.color, border: `1px solid ${s.color}33`, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

function SeverityPill({ severityId }) {
  const s = SEVERITY_BY_ID[severityId];
  if (!s) return null;
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
      background: s.color + '22', color: s.color, textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>{s.label}</span>
  );
}

export default function DamagesPage() {
  const { damages, vehicles, openModal, canEdit } = useApp();

  const [search, setSearch]       = useState('');
  const [filterStatus, setFilterStatus]     = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterVehicle, setFilterVehicle]   = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]     = useState('');

  // ── Status buckets — every status falls into exactly one ──────────────
  // Open    = still being chased (money may still come)
  // Paid    = money recovered (paid + legacy 'resolved')
  // Closed  = finalized with no payment (waived / dismissed)
  // → Open + Paid + Closed = Total (always reconciles)
  const OPEN_STATUSES   = ['reported', 'charging', 'disputed', 'insurance'];
  const PAID_STATUSES   = ['paid', 'resolved'];
  const CLOSED_STATUSES = ['waived', 'dismissed'];

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return damages.filter(d => {
      if (filterStatus !== 'all') {
        // Bucket filters: 'any_open' / 'any_paid' / 'any_closed' match groups; else exact status match
        if (filterStatus === 'any_open'   ? !OPEN_STATUSES.includes(d.status)
          : filterStatus === 'any_paid'   ? !PAID_STATUSES.includes(d.status)
          : filterStatus === 'any_closed' ? !CLOSED_STATUSES.includes(d.status)
          : d.status !== filterStatus) return false;
      }
      if (filterSeverity !== 'all' && d.severity !== filterSeverity) return false;
      if (filterVehicle !== 'all' && d.vid !== filterVehicle) return false;
      if (filterDateFrom && d.reportedDate < filterDateFrom) return false;
      if (filterDateTo && d.reportedDate > filterDateTo) return false;
      if (q) {
        const v = vehicles.find(vv => vv.id === d.vid);
        const haystack = [
          d.description, d.location, d.customerName, d.notes,
          v?.name, v?.plate, d.id,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [damages, vehicles, search, filterStatus, filterSeverity, filterVehicle, filterDateFrom, filterDateTo]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const open   = damages.filter(d => OPEN_STATUSES.includes(d.status));
    const paid   = damages.filter(d => PAID_STATUSES.includes(d.status));
    const closed = damages.filter(d => CLOSED_STATUSES.includes(d.status));
    // YTD cost = money ACTUALLY paid this year (not waived, not pending)
    const ytdCost = paid
      .filter(d => d.reportedDate?.startsWith(new Date().getFullYear().toString()))
      .reduce((s, d) => s + (parseFloat(d.finalCost) || parseFloat(d.estimatedCost) || 0), 0);
    const openCost = open.reduce((s, d) => s + (parseFloat(d.estimatedCost) || 0), 0);
    return { total: damages.length, open: open.length, paid: paid.length, closed: closed.length, ytdCost, openCost };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bucket arrays are module constants
  }, [damages]);

  const editable = canEdit ? canEdit('damages') : true;
  const activeFilters = (filterStatus !== 'all' ? 1 : 0)
                      + (filterSeverity !== 'all' ? 1 : 0)
                      + (filterVehicle !== 'all' ? 1 : 0)
                      + (filterDateFrom ? 1 : 0)
                      + (filterDateTo ? 1 : 0);

  function resetFilters() {
    setSearch('');
    setFilterStatus('all');
    setFilterSeverity('all');
    setFilterVehicle('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  }

  return (
    <div style={{ padding: '20px 24px' }}>
      <PageHeader
        title="Damages"
        icon="🔧"
        subtitle="Track and resolve vehicle damage across your fleet. Paid damages still show as pre-existing on next rental until marked repaired."
        actions={editable && (
          <button className="btn btn-primary" onClick={() => openModal('damage')}>+ Report Damage</button>
        )}
      />

      {/* ── Stat cards — clickable filters · Open + Paid + Closed = Total ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <StatCard
          label="Total damages" value={stats.total}
          desc={(filterStatus !== 'all' || filterSeverity !== 'all' || filterVehicle !== 'all' || filterDateFrom || filterDateTo || search)
            ? 'Click to clear all filters'
            : `${stats.open} open + ${stats.paid} paid + ${stats.closed} closed`}
          active={filterStatus === 'all' && filterSeverity === 'all' && filterVehicle === 'all' && !filterDateFrom && !filterDateTo && !search}
          onClick={resetFilters}
        />
        <StatCard
          label="Open" value={stats.open} variant="am"
          desc={filterStatus === 'any_open' ? 'Filtering — click to clear' : (stats.openCost ? `€${stats.openCost.toFixed(0)} pending` : 'Unresolved damages')}
          active={filterStatus === 'any_open'}
          onClick={() => setFilterStatus(filterStatus === 'any_open' ? 'all' : 'any_open')}
        />
        <StatCard
          label="Paid" value={stats.paid} variant="gr"
          desc={filterStatus === 'any_paid'
            ? 'Filtering — click to clear'
            : (stats.ytdCost ? `€${stats.ytdCost.toFixed(0)} recovered ${new Date().getFullYear()}` : 'Customer / insurance paid')}
          active={filterStatus === 'any_paid'}
          onClick={() => setFilterStatus(filterStatus === 'any_paid' ? 'all' : 'any_paid')}
        />
        <StatCard
          label="Closed" value={stats.closed}
          desc={filterStatus === 'any_closed' ? 'Filtering — click to clear' : 'Waived / dismissed — no charge'}
          active={filterStatus === 'any_closed'}
          onClick={() => setFilterStatus(filterStatus === 'any_closed' ? 'all' : 'any_closed')}
        />
      </div>

      {/* ── Severity quick-chips — single-click filter by severity ─── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.05em', marginRight: 4 }}>Severity:</span>
        {DAMAGE_SEVERITIES.map(s => {
          const count = damages.filter(d => d.severity === s.id).length;
          const active = filterSeverity === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setFilterSeverity(active ? 'all' : s.id)}
              style={{
                padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                border: `1.5px solid ${active ? s.color : 'var(--bd)'}`,
                borderRadius: 20,
                background: active ? s.color + '22' : 'var(--sur)',
                color: active ? s.color : 'var(--tx2)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
              <span>{s.label}</span>
              <span style={{
                fontSize: 10, fontWeight: 700,
                background: active ? s.color : 'var(--sur2)',
                color: active ? '#fff' : 'var(--tx3)',
                borderRadius: 10, padding: '1px 7px',
                fontFamily: "'DM Mono',monospace",
              }}>{count}</span>
            </button>
          );
        })}
        {filterSeverity !== 'all' && (
          <button
            type="button"
            onClick={() => setFilterSeverity('all')}
            style={{ padding: '5px 10px', fontSize: 10, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--bd)', borderRadius: 20, background: 'transparent', color: 'var(--tx3)' }}
          >✕ Clear</button>
        )}
      </div>

      {/* ── Filter bar ───────────────────────────────────────────────── */}
      <div style={{
        background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 12,
        padding: '12px 14px', marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
      }}>
        <div className="fg" style={{ flex: '1 1 220px', margin: 0 }}>
          <label className="fl">Search</label>
          <input type="text" className="fi" placeholder="🔍 Description, location, vehicle, customer…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="fg" style={{ flex: '0 0 140px', margin: 0 }}>
          <label className="fl">Status</label>
          <select className="fi" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All</option>
            <optgroup label="Buckets">
              <option value="any_open">⚠️ Open (unresolved)</option>
              <option value="any_paid">✅ Paid (money recovered)</option>
              <option value="any_closed">🚫 Closed (waived / dismissed)</option>
            </optgroup>
            <optgroup label="Exact status">
              {DAMAGE_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </optgroup>
          </select>
        </div>
        <div className="fg" style={{ flex: '0 0 130px', margin: 0 }}>
          <label className="fl">Severity</label>
          <select className="fi" value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}>
            <option value="all">All</option>
            {DAMAGE_SEVERITIES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div className="fg" style={{ flex: '0 0 160px', margin: 0 }}>
          <label className="fl">Vehicle</label>
          <select className="fi" value={filterVehicle} onChange={e => setFilterVehicle(e.target.value)}>
            <option value="all">All vehicles</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.name || v.plate}</option>)}
          </select>
        </div>
        <div className="fg" style={{ flex: '0 0 130px', margin: 0 }}>
          <label className="fl">From</label>
          <input type="date" className="fi" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
        </div>
        <div className="fg" style={{ flex: '0 0 130px', margin: 0 }}>
          <label className="fl">To</label>
          <input type="date" className="fi" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {activeFilters > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={resetFilters}>
              Reset ({activeFilters})
            </button>
          )}
        </div>
      </div>

      {/* ── List ─────────────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="empty">
          <div className="empty-ico">{damages.length === 0 ? '🎉' : '🔍'}</div>
          <div className="empty-title">
            {damages.length === 0 ? 'No damages — clean fleet!' : 'No damages match your filters'}
          </div>
          <div className="empty-sub">
            {damages.length === 0
              ? 'When a customer returns a vehicle with new damage, log it here so you can charge, dispute, or claim insurance later.'
              : 'Try clearing some filters above.'}
          </div>
          {damages.length === 0 && editable && (
            <button className="btn btn-primary" onClick={() => openModal('damage')}>+ Report First Damage</button>
          )}
          {damages.length > 0 && activeFilters > 0 && (
            <button className="btn btn-ghost" onClick={resetFilters}>Clear filters</button>
          )}
        </div>
      ) : (
        <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '70px 1.4fr 1fr 1fr 100px 90px 110px 70px 60px',
            gap: 8, padding: '10px 14px', background: 'var(--sur2)',
            fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.05em',
            borderBottom: '1px solid var(--bd)',
          }}>
            <div>Date</div>
            <div>Vehicle / Damage</div>
            <div>Customer / Rental</div>
            <div>Location</div>
            <div>Severity</div>
            <div style={{ textAlign: 'right' }}>Cost</div>
            <div>Payment</div>
            <div>Repair</div>
            <div></div>
          </div>
          {filtered.map(d => {
            const v = vehicles.find(vv => vv.id === d.vid);
            const type = TYPE_BY_ID[d.type];
            const cost = d.finalCost ?? d.estimatedCost ?? 0;
            return (
              <div key={d.id}
                onClick={() => editable && openModal('damage', d.id)}
                style={{
                  display: 'grid', gridTemplateColumns: '70px 1.4fr 1fr 1fr 100px 90px 110px 70px 60px',
                  gap: 8, padding: '12px 14px', alignItems: 'center',
                  fontSize: 12, borderBottom: '1px solid var(--bd)',
                  cursor: editable ? 'pointer' : 'default',
                  transition: 'background .12s',
                }}
                onMouseEnter={e => editable && (e.currentTarget.style.background = 'var(--sur2)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ fontSize: 11, color: 'var(--tx2)' }}>
                  {d.reportedDate ? new Date(d.reportedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={{ fontSize: 14 }}>{type?.icon || '⚠️'}</span>
                    {type?.label || 'Damage'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {v?.name || '—'}
                  </div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {d.customerName || <span style={{ color: 'var(--tx3)' }}>—</span>}
                  </div>
                  {d.rentalId && (
                    <div style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>{d.rentalId}</div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--tx2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.location || '—'}
                </div>
                <div>
                  <SeverityPill severityId={d.severity} />
                </div>
                <div style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>
                  €{Number(cost).toFixed(0)}
                </div>
                <div>
                  <StatusPill statusId={d.status} />
                </div>
                <div>
                  {d.repaired || d.status === 'resolved' ? (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: '#d1fae5', color: '#10b981', whiteSpace: 'nowrap' }}>
                      ✓ Repaired
                    </span>
                  ) : (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap' }}>
                      🔧 On car
                    </span>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  {editable && <span style={{ color: 'var(--tx3)', fontSize: 14 }}>›</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
